import { useReducer, useCallback } from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Unstable_Grid2'
import { DatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import DraggableModal from '../../generic/DraggableModal'
import moment from 'moment'
import { backendPostWithoutRefresh } from '../../../helpers/callBackend'
import { errorMessage } from '../../../helpers/errorMessageFunc'

const dateFormat = 'YYYY-MM-DD'

const ACTION_TYPES = {
  SET_START_DATE: 'SET_START_DATE',
  SET_END_DATE: 'SET_END_DATE',
  SET_LOADING: 'SET_LOADING',
}

const setStartDate = (momentObject) => {
  return {
    type: ACTION_TYPES.SET_START_DATE,
    payload: momentObject.format(dateFormat),
  }
}

const setEndDate = (momentObject) => {
  return {
    type: ACTION_TYPES.SET_END_DATE,
    payload: momentObject.format(dateFormat),
  }
}

const setLoading = (loading) => {
  return {
    type: ACTION_TYPES.SET_LOADING,
    payload: loading,
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    default:
      return state
    case ACTION_TYPES.SET_START_DATE:
      return {
        ...state,
        startDate: action.payload,
      }
    case ACTION_TYPES.SET_END_DATE:
      return {
        ...state,
        endDate: action.payload,
      }
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }
  }
}

const datePickerStyle = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
  },
}

const RecalibrateBalancesModal = ({
  modalVisible,
  handleCancelModal,
  bankAccountName,
  bankAccountId,
  onRecalibrate,
}) => {
  const initialState = {
    startDate: moment().format(dateFormat),
    endDate: moment().format(dateFormat),
    loading: false,
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  const handleRecalibrate = useCallback(async () => {
    // Add recalibration logic here
    try {
      dispatch(setLoading(true))
      const data = {
        bank_account_id: bankAccountId,
        start_date: state.startDate,
        end_date: state.endDate,
      }

      const recalResp = await backendPostWithoutRefresh('/api/transactions/recalibrate/', data)
      const updatedTransactions = recalResp.data?.data
      onRecalibrate(updatedTransactions)
    } catch (error) {
      if (error.status === 504) {
        errorMessage('Recalibration timed out, try a shorter timeframe.', 5)
      } else {
        errorMessage('Something went wrong with transaction balance recalibration.')
      }
      dispatch(setLoading(false))
      handleCancelModal()
    }
    handleCancelModal()
    dispatch(setLoading(false))
  }, [dispatch, state, bankAccountId])

  const modalContent = (
    <Stack spacing={5} alignItems='start'>
      <Typography align='center'>
        By clicking submit, the balance for each transaction will be recalculated from the current balance. The most
        recent transaction's balance will have the account's current balance. The transactions will be calculated
        incrementally.
      </Typography>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Grid container spacing={3} alignItems='center'>
          <Grid item xs={4}>
            <Typography align='right'>Start Date</Typography>
          </Grid>
          <Grid item xs={8}>
            <DatePicker
              onChange={(newValue) => dispatch(setStartDate(newValue))}
              defaultValue={moment()}
              sx={datePickerStyle}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography align='right'>End Date</Typography>
          </Grid>
          <Grid item xs={8}>
            <DatePicker
              onChange={(newValue) => dispatch(setEndDate(newValue))}
              defaultValue={moment()}
              sx={datePickerStyle}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>
    </Stack>
  )

  return (
    <DraggableModal
      modalTitle={`Are you sure you want to recalibrate transaction balances for ${bankAccountName}?`}
      modalVisible={modalVisible}
      handleCancelModal={handleCancelModal}
      content={modalContent}
      width='auto'
      justifyActions='start'
      actions={
        <>
          <LoadingButton
            onClick={handleRecalibrate}
            disabled={!state.startDate || !state.endDate}
            disableElevation
            loading={state.loading}
            variant='contained'
            sx={{ fontSize: 14, textTransform: 'none' }}
          >
            Submit
          </LoadingButton>
        </>
      }
    />
  )
}

export default RecalibrateBalancesModal
